<template>
  <HeaderBar />

  <section class="carvia-container">
    <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
      <Input
        v-model="search"
        :placeholder="t('search')"
        class="w-full max-sm:col-span-2"
        variant="ghost"
      />

      <Input
        v-model="bookingNumber"
        :placeholder="t('bookingNumber')"
        class="w-full max-sm:hidden"
        variant="ghost"
      />

      <Datepicker
        v-if="activeTab === Tab.ACTIVE"
        :model-value="filterDate"
        :date-format="dateFormat"
        week-numbers
        :clearable="false"
        class="w-full"
        @update:model-value="setfilterDate"
      />
      <DateRangeFilterPicker
        v-else-if="activeTab === Tab.ALL"
        v-model:start-date="dateRangeStart"
        v-model:end-date="dateRangeEnd"
        :placeholder="t('dateRange')"
        class="max-sm:col-span-2"
      />
      <div v-else></div>

      <StationLegalEntitySwitcher v-model="currentStationId" />
    </div>

    <Tabs v-model="activeTab" :tabs="tabs" class="mb-4 mt-6" />

    <BookingsList v-if="activeTab !== Tab.TRANSFERS_WITHOUT_DRIVER" :query="getBookingsQuery" />
    <TransfersList v-else :query="transfersQuery" />
  </section>
</template>

<script lang="ts" setup>
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import Input from '@/components/Input.vue';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import { useI18n } from 'vue-i18n';
import { BOOKINGS_QUERY_KEY, useBookingsActionNeededCounts } from '@/queries/use-bookings';
import { useUiStore } from '@/stores/ui.store';
import { computed, onActivated, ref, watch, watchEffect } from 'vue';
import StationLegalEntitySwitcher from '@/components/StationLegalEntitySwitcher.vue';
import { useLegalEntityFilter } from '@/hooks/use-legal-entity-filter';
import { useDateFormat } from '@/hooks/use-date-format';
import BookingsList from './BookingsList.vue';
import TransfersList from './TransfersList.vue';
import { useGetBookingsQuery } from './use-get-bookings-query';
import { useGetUnassignedLongTransfersQuery } from './use-get-unassigned-long-transfers-query';
import { Tab } from './tab.enum';
import { refDebounced, watchDebounced } from '@vueuse/core';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import { useAbility } from '@casl/vue';
import { subject } from '@casl/ability';
import { BookingStatus } from '@carvia/ros-client-types';
import { sortBy } from 'lodash';
import Datepicker from '@/components/Datepicker.vue';
import DateRangeFilterPicker from '@/components/DateRangeFilterPicker.vue';
import { ALLOCATIONS_QUERY_KEY } from '@/queries/use-allocations';
import { useQueryClient } from '@tanstack/vue-query';

const { t } = useI18n();
const queryClient = useQueryClient();
const currentStationId = useRouteQueryRef('stationId', undefined, 'string');
const uiStore = useUiStore();
const ability = useAbility();

watchEffect(() => {
  uiStore.setHeaderTitle(t('bookings'));
});

const dateFormat = useDateFormat();
const filterDate = useRouteQueryRef('filterDate', new Date(), 'date');

const setfilterDate = (value: Date) => {
  filterDate.value = value;
};

const dateRangeStart = useRouteQueryRef('dateRangeStart', null, 'date');
const dateRangeEnd = useRouteQueryRef('dateRangeEnd', null, 'date');

const canListManualInvoicing = computed(() => ability.can('ros', 'ListManualInvoiceBooking'));
const activeTab = ref<Tab>(Tab.ALL);

const search = useRouteQueryRef('search', '', 'string');
const searchQuery = refDebounced(search, 500);
watch(search, (newSearch) => {
  if (newSearch) {
    bookingNumber.value = undefined;
  }
});

const bookingNumber = useRouteQueryRef('bookingNumber', undefined, 'string');
const bookingNumberQuery = ref<number | undefined>(
  bookingNumber.value ? parseInt(bookingNumber.value) : undefined,
);
watch(
  bookingNumber,
  () => {
    if (bookingNumber.value) {
      search.value = '';
      if (activeTab.value !== Tab.TRANSFERS_WITHOUT_DRIVER) {
        activeTab.value = Tab.ALL;
      }
    }
  },
  { immediate: true },
);
watchDebounced(
  bookingNumber,
  () => {
    bookingNumberQuery.value = !isNaN(parseInt(bookingNumber.value ?? ''))
      ? parseInt(bookingNumber.value ?? '')
      : undefined;
  },
  { debounce: 500 },
);

const { legalEntityId } = useLegalEntityFilter();
const getBookingsQuery = useGetBookingsQuery(
  activeTab,
  searchQuery,
  currentStationId,
  filterDate,
  bookingNumberQuery,
  dateRangeStart,
  dateRangeEnd,
  legalEntityId,
);
const transfersQuery = useGetUnassignedLongTransfersQuery(
  currentStationId,
  searchQuery,
  bookingNumberQuery,
  legalEntityId,
);

watch(activeTab, () => {
  if (activeTab.value !== Tab.ALL && activeTab.value !== Tab.TRANSFERS_WITHOUT_DRIVER) {
    bookingNumber.value = undefined;
  }
});

const { data: tabCounts } = useBookingsActionNeededCounts(
  currentStationId,
  searchQuery,
  bookingNumberQuery,
  legalEntityId,
);

const tabBadge = (count?: number) =>
  count == null ? undefined : count > 99 ? '99+' : String(count);

onActivated(() => {
  void queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [ALLOCATIONS_QUERY_KEY] });
});

const tabs = computed<TabItem[]>(() => {
  const tabs = [
    {
      title: t('all'),
      id: Tab.ALL,
      sort: 1,
    },
    {
      title: t('forReview'),
      id: Tab.FOR_REVIEW,
      badge: tabBadge(tabCounts.value?.forReviewCount),
      sort: 2,
    },
    {
      title: t('noCar'),
      id: Tab.NO_CAR,
      badge: tabBadge(tabCounts.value?.noCarCount),
      sort: 3,
    },
    {
      title: t('active'),
      id: Tab.ACTIVE,
      sort: 4,
    },
    {
      title: t('transfersWithoutDriver'),
      id: Tab.TRANSFERS_WITHOUT_DRIVER,
      badge: tabBadge(tabCounts.value?.transfersWithoutDriverCount),
      sort: 5,
    },
  ];
  if (
    ability.can(
      'list',
      subject('Booking', {
        status: BookingStatus.DRAFT,
      }),
    )
  ) {
    tabs.push({
      title: t('unpaidOnline'),
      id: Tab.UNPAID_ONLINE,
      sort: 6,
    });
  }
  if (canListManualInvoicing.value) {
    tabs.push({
      title: t('manualInvoicing'),
      id: Tab.MANUAL_INVOICING,
      sort: 7,
    });
  }
  return sortBy(tabs, 'sort');
});
</script>

<i18n lang="json">
{
  "en": {
    "bookings": "Bookings",
    "search": "name/car/licence plate/reference",
    "allStations": "All Stations",
    "unpaidOnline": "Unpaid online",
    "forReview": "For review",
    "active": "Active",
    "noCar": "No car",
    "transfersWithoutDriver": "Transfers w/o driver",
    "all": "All",
    "bookingNumber": "Booking Number",
    "manualInvoicing": "Man. Invoicing",
    "dateRange": "Date range"
  },
  "de": {
    "bookings": "Buchungen",
    "search": "Name/Auto/Kennzeichen/Referenz",
    "allStations": "Alle Stationen",
    "unpaidOnline": "Unbez. Online",
    "forReview": "Zur Prüfung",
    "active": "Aktiv",
    "noCar": "Kein Auto",
    "transfersWithoutDriver": "Transfers o. Fahrer",
    "all": "Alle",
    "bookingNumber": "Buchungsnummer",
    "manualInvoicing": "Man. Abrechnung",
    "dateRange": "Datumsbereich"
  },
  "es": {
    "bookings": "Reservas",
    "search": "nombre/coche/matrícula/referencia",
    "allStations": "Todas las estaciones",
    "unpaidOnline": "Online sin pagar",
    "forReview": "Pendiente de revisión",
    "active": "Activas",
    "noCar": "Sin coche",
    "transfersWithoutDriver": "Traslados s. cond.",
    "all": "Todas",
    "bookingNumber": "N.º de reserva",
    "manualInvoicing": "Man. Facturación",
    "dateRange": "Rango de fechas"
  }
}
</i18n>
